import request, { HttpMethod } from '@app/libs/request';
import config, { serverUrl } from '@app/config';
import Log from '@app/libs/logger';
import { SupplierRegistrationProcessFormType } from '@app/domain/commonSupplierRegistration';
import {
  API_VERSION_HEADER,
  CAPTCHA_TOKEN_HEADER,
  ReducedSupplierRegistrationProcess,
  ReducedSupplierRegistrationProcessServerResponse,
  SupplierRegistrationProcessRequest,
  WEB_REFERRER_HEADER,
} from '@supplierRegistration/domain/supplierRegistration';
import { SupplierRegistrationAsyncInitiateValidationProcessRequest } from '@supplierRegistration/domain/supplierRegistrationAsyncInitiateValidationProcess';
import { RawAxiosRequestHeaders } from 'axios';

export async function storeSupplierRegistrationProcessRequest(
  supplierData: SupplierRegistrationProcessRequest,
  validationType: SupplierRegistrationProcessFormType,
  language: string | undefined,
  captchaToken: string | null | undefined,
): Promise<ReducedSupplierRegistrationProcessServerResponse> {
  const headers: RawAxiosRequestHeaders = {
    [WEB_REFERRER_HEADER]: document.referrer,
    [API_VERSION_HEADER]: 'v2',
  };

  if (config.featureFlags?.supplierRegistrationCaptcha && captchaToken) {
    headers[CAPTCHA_TOKEN_HEADER] = captchaToken;
  } else if (config.featureFlags?.supplierRegistrationCaptcha) {
    Log.exception(new Error('Sending supplier validation request without reCAPTCHA token'));
  }

  return request<ReducedSupplierRegistrationProcessServerResponse>(serverUrl, `/api/supplier-validation`, {
    data: supplierData,
    headers,
    params: { type: validationType, language: language },
    method: HttpMethod.post,
    dontRedirectToLogin: true,
    suppressNotification: true,
    errorsHandler: {
      default: {
        message: 'Could not request for supplier validation.',
      },
    },
  });
}

export async function storeSupplierRegistrationProcessRequestFromAsyncInitiateValidation(
  supplierData: SupplierRegistrationAsyncInitiateValidationProcessRequest,
  asyncProcessId: string,
  language: string | undefined,
  captchaToken: string | null | undefined,
): Promise<ReducedSupplierRegistrationProcessServerResponse> {
  const headers: RawAxiosRequestHeaders = {
    [WEB_REFERRER_HEADER]: document.referrer,
    [API_VERSION_HEADER]: 'v2',
  };

  if (config.featureFlags?.supplierRegistrationCaptcha && captchaToken) {
    headers[CAPTCHA_TOKEN_HEADER] = captchaToken;
  } else if (config.featureFlags?.supplierRegistrationCaptcha) {
    Log.exception(new Error('Sending supplier validation request without reCAPTCHA token'));
  }

  return request<ReducedSupplierRegistrationProcessServerResponse>(serverUrl, `/api/supplier-validation/async/:asyncProcessId`, {
    data: supplierData,
    pathParams: { asyncProcessId },
    headers,
    params: { language },
    method: HttpMethod.post,
    dontRedirectToLogin: true,
    suppressNotification: true,
    errorsHandler: {
      default: {
        message: 'Could not request for supplier validation async.',
      },
    },
  });
}

export function transformToReducedSupplierRegistrationProcess(
  serverResponse: ReducedSupplierRegistrationProcessServerResponse,
): ReducedSupplierRegistrationProcess {
  return {
    registrationNumber: serverResponse.registrationNumber,
    displayInstructionType: serverResponse.displayInstructionType,
  };
}
